import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient } from "../../constants.ts";
import {
  Button,
  CollapsibleCard,
  inlineCodeClass,
  inputBaseClass,
  Spinner,
} from "./components.tsx";

const formatDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

type TokenRow = { id: string; name: string; createdAt: number };

type ApiTokensSectionProps =
  & {
    title: string;
    description: preact.ComponentChildren;
    token: string;
    createButtonLabel: string;
    createPlaceholder: string;
    collapsible?: boolean;
    defaultOpen?: boolean;
    subtitle?: preact.ComponentChildren;
  }
  & (
    | { mode: "user" }
    | { mode: "bot"; botId: string }
  );

type ListTokensResult =
  | { tokens: TokenRow[] }
  | { success: true; tokens: TokenRow[] }
  | { success: false; tokens: never[]; error: string };

type CreateTokenResult =
  | { success: true; token: string; tokenId: string }
  | { success: false; error: string };

type RevokeTokenResult = { success: boolean; error?: string };

const botIdFromProps = (props: ApiTokensSectionProps) =>
  props.mode === "bot" ? props.botId : "";

const isErrorListResult = (
  result: ListTokensResult,
): result is { success: false; tokens: never[]; error: string } =>
  "success" in result && result.success === false;

const tokensFromListResult = (result: ListTokensResult) => result.tokens;

const loadTokens = (props: ApiTokensSectionProps) =>
  props.mode === "user"
    ? apiClient({
      endpoint: "list-api-tokens",
      token: props.token,
      payload: {},
    })
    : apiClient({
      endpoint: "list-bot-api-tokens",
      token: props.token,
      payload: { botId: botIdFromProps(props) },
    });

const createToken = (props: ApiTokensSectionProps, name: string) =>
  props.mode === "user"
    ? apiClient({
      endpoint: "create-api-token",
      token: props.token,
      payload: { name },
    })
    : apiClient({
      endpoint: "create-bot-api-token",
      token: props.token,
      payload: { botId: botIdFromProps(props), name },
    });

const revokeToken = (props: ApiTokensSectionProps, tokenId: string) =>
  props.mode === "user"
    ? apiClient({
      endpoint: "revoke-api-token",
      token: props.token,
      payload: { tokenId },
    })
    : apiClient({
      endpoint: "revoke-bot-api-token",
      token: props.token,
      payload: { botId: botIdFromProps(props), tokenId },
    });

export const ApiTokensSection = (props: ApiTokensSectionProps) => {
  const {
    title,
    description,
    createButtonLabel,
    createPlaceholder,
  } = props;
  const botId = botIdFromProps(props);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    const result = await loadTokens(props) as ListTokensResult;
    if (isErrorListResult(result)) {
      toast.error(result.error);
      return;
    }
    setTokens(tokensFromListResult(result));
    setLoaded(true);
  };

  useEffect(() => {
    setLoaded(false);
    refresh();
  }, [props.mode, props.token, botId]);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const result = await createToken(props, trimmed) as CreateTokenResult;
      if (result.success) {
        setNewToken(result.token);
        setName("");
        refresh();
      } else {
        toast.error(result.error);
      }
    } catch (e) {
      toast.error("Failed to create token");
      console.error(e);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = (tokenId: string, tokenName: string) =>
    toast.promise(
      revokeToken(props, tokenId).then((result: RevokeTokenResult) => {
        if (!result.success) throw new Error(result.error);
        refresh();
      }),
      {
        loading: "Revoking...",
        success: `Revoked token "${tokenName}"`,
        error: "Failed to revoke token",
      },
    );

  const content = (
    <>
      <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-4">
        {description}
      </p>

      {newToken && (
        <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg">
          <p className="text-sm font-medium text-success mb-1">
            Token created. Copy it now — it won't be shown again.
          </p>
          <code className="block text-xs bg-surface-light dark:bg-surface p-2 rounded border border-success/30 break-all select-all">
            {newToken}
          </code>
          <button
            type="button"
            onClick={() => setNewToken(null)}
            className="mt-2 text-xs text-success hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <input
          type="text"
          placeholder={createPlaceholder}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          className={`flex-1 max-w-md ${inputBaseClass}`}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
        />
        <Button disabled={creating || !name.trim()} onClick={handleCreate}>
          {creating ? "Creating..." : createButtonLabel}
        </Button>
      </div>

      {!loaded && (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      )}
      {loaded && tokens.length > 0 && (
        <div className="border border-border-subtle-light dark:border-border-subtle rounded-lg divide-y divide-border-subtle-light dark:divide-border-subtle">
          {tokens.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between px-4 py-3"
            >
              <div>
                <p className="font-medium text-sm">{t.name}</p>
                <p className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                  Created {formatDate(t.createdAt)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(t.id, t.name)}
                className="text-sm text-danger hover:text-danger/80"
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  return props.collapsible
    ? (
      <CollapsibleCard
        title={title}
        subtitle={props.subtitle ?? (loaded
          ? `${tokens.length} ${tokens.length === 1 ? "token" : "tokens"}`
          : undefined)}
        defaultOpen={props.defaultOpen}
      >
        {content}
      </CollapsibleCard>
    )
    : (
      <div className="bg-surface-raised-light dark:bg-surface-raised rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-display font-semibold mb-4">{title}</h2>
        {content}
      </div>
    );
};

export const Prompt2botClientDescription = () => (
  <>
    Create tokens to use with the{" "}
    <code className={inlineCodeClass}>
      @prompt2bot/client
    </code>{" "}
    library for programmatic bot creation.
  </>
);
